import React, { useEffect, useState } from 'react'
import { FiDownload } from 'react-icons/fi'
import { Lock, LockOpen } from '@mui/icons-material'
import ToggleOffIcon from '@mui/icons-material/ToggleOff'
import ToggleOnIcon from '@mui/icons-material/ToggleOn'
import CardContent from '@mui/material/CardContent'
import Card from '@mui/material/Card'
import { toast } from 'react-toastify'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import CheckIcon from '@mui/icons-material/Check'
import PersonIcon from '@mui/icons-material/Person'
import Typography from '@mui/material/Typography'
import ClearIcon from '@mui/icons-material/Clear'
import Box from '@mui/material/Box'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import dayjs from 'dayjs'
import api from '@/services/api'
import Notfound from '../../../Notfound'
import { TableShimmer } from '../../../ShimmerUI'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Pagination,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material'
import PercentIcon from '@mui/icons-material/Percent'
import RowsPerPageDropdown from '../RowsPerPageDropdown'
const ProviderManagementPage: React.FC = () => {
  const perPage = 10
  const prevSize = sessionStorage.getItem('prevSize')
  const ourPageSize = prevSize ? parseInt(prevSize, 10) : 10
  const [pageSize, setPageSize] = useState(10)
  const [services, setServices] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [loadingForShimmer, setLoadingForShimmer] = useState(true)
  const [therapists, setTherapists] = useState([])

  const [error, setError] = useState('')
  const [searchKeyword, setSearchKeyword] = useState<string>('')
  const [selectedAccountStatus, setSelectedAccountStatus] = useState('')
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [licenseExpiryDate, setLicenseExpiryDate] = useState<Date | null>(null)

  const [totalRecords, setTotalRecords] = useState(0)
  const [totalActiveRecords, setTotalActiveRecords] = useState(0)
  const [totalInactiveRecords, setTotalInactiveRecords] = useState(0)
  const [openToggle, setOpenToggle] = useState(false) // Dialog open state
  const [sortField, setSortField] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [selectedTherapistId, setSelectedTherapistId] = useState<string | null>(
    null
  ) // Selected therapist's ID
  const [isActive, setIsActive] = useState<boolean>(false) // Current status of the therapist
  const [loading, setLoading] = useState<boolean>(false) // Loading state during API request
  const [exportLoading, setExportLoading] = useState<boolean>(false) // Loading state for export
  const [openCommissionDialog, setOpenCommissionDialog] = useState(false) // Commission dialog state
  const [monthlyCommission, setMonthlyCommission] = useState<string>('') // Monthly commission value
  const [yearlyCommission, setYearlyCommission] = useState<string>('') // Yearly commission value
  const [commissionLoading, setCommissionLoading] = useState<boolean>(false) // Loading state for commission update

  const handleExport = async () => {
    setExportLoading(true)
    try {
      const params: Record<string, any> = {
        accountStatus:
          selectedAccountStatus === 'Active'
            ? true
            : selectedAccountStatus === 'Inactive'
              ? false
              : undefined,
      }

      // If search keyword exists, add it to params
      if (searchKeyword?.trim()) {
        params.search = searchKeyword
      }

      if (startDate) {
        params.startDate = getFormattedDateForAPI(startDate)
      }

      if (endDate) {
        params.endDate = getFormattedDateForAPI(endDate)
      }

      if (licenseExpiryDate) {
        params.expiry = getFormattedDateForAPI(licenseExpiryDate)
      }

      const response = await api.get('admin/therapists/export', {
        responseType: 'blob',
        params,
      })

      // Create a blob from the response
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'text/csv',
      })

      // Create a download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      // Get filename from Content-Disposition header or use default
      // const contentDisposition = response.headers['content-disposition']
      let filename = 'providers_export.csv'
      // if (contentDisposition) {
      //   const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i)
      //   if (filenameMatch) {
      //     filename = filenameMatch[1]
      //   }
      // }

      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      toast.success('Export completed successfully')
    } catch (error) {
      console.error('Error exporting providers:', error)
      toast.error('Failed to export providers data')
    } finally {
      setExportLoading(false)
    }
  }

  const getFormattedDateForAPI = (date: Date | null): string | null => {
    if (!date) return null

    const year = date.getFullYear()
    const month = ('0' + (date.getMonth() + 1)).slice(-2)
    const day = ('0' + date.getDate()).slice(-2)

    return `${year}-${month}-${day}` // YYYY-MM-DD format
  }
  const handleAccountStatusChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedAccountStatus(event.target.value)
  }

  const handleOpenToggle = (providerId: string, currentStatus: boolean) => {
    setSelectedTherapistId(providerId) // Set the selected therapist to toggle
    setIsActive(currentStatus) // Set the current status of the therapist (active or inactive)
    setOpenToggle(true) // Open the confirmation dialog
  }

  const handleConfirmToggle = async () => {
    if (!selectedTherapistId) return // Exit if no therapist is selected
    setLoading(true) // Start loading

    const newStatus = !isActive // Toggle the status
    try {
      const response = await api.patch(
        `/admin/therapist/${selectedTherapistId}/status`,
        { isActive: newStatus }
      )

      if (response.status === 200) {
        setIsActive(newStatus) // Toggle status locally
        toast.success(
          `Provider status updated to ${newStatus ? 'Active' : 'Inactive'}`
        )
      }

      setOpenToggle(false) // Close the dialog after success
      fetchTherapists() // Optionally refresh the therapist list
    } catch (error) {
      console.error('Error updating the provider status:', error)
      toast.error('Failed to update provider status')
    } finally {
      setLoading(false) // Stop loading
    }
  }

  const handleOpenCommissionDialog = (therapist: any) => {
    setSelectedTherapistId(therapist._id)
    setMonthlyCommission(therapist.monthlyCommission?.toString() || '')
    setYearlyCommission(therapist.yearlyCommission?.toString() || '')
    setOpenCommissionDialog(true)
  }

  const handleCloseCommissionDialog = () => {
    setOpenCommissionDialog(false)
    setSelectedTherapistId(null)
    setMonthlyCommission('')
    setYearlyCommission('')
  }

  const handleUpdateCommission = async () => {
    if (!selectedTherapistId) return

    // Validate inputs
    if (!monthlyCommission.trim() || !yearlyCommission.trim()) {
      toast.error('Please enter both monthly and yearly commission values')
      return
    }

    const monthlyValue = parseFloat(monthlyCommission)
    const yearlyValue = parseFloat(yearlyCommission)

    if (
      isNaN(monthlyValue) ||
      isNaN(yearlyValue) ||
      monthlyValue < 0 ||
      yearlyValue < 0
    ) {
      toast.error('Please enter valid commission values (numbers >= 0)')
      return
    }

    setCommissionLoading(true)
    try {
      const response = await api.patch(
        `/admin/therapist/${selectedTherapistId}/commission`,
        {
          monthlyCommission: monthlyValue,
          yearlyCommission: yearlyValue,
        }
      )

      if (response.status === 200) {
        toast.success('Commission updated successfully')
        handleCloseCommissionDialog()
        fetchTherapists() // Refresh the therapist list
      }
    } catch (error: any) {
      console.error('Error updating commission:', error)
      toast.error(
        error?.response?.data?.message || 'Failed to update commission'
      )
    } finally {
      setCommissionLoading(false)
    }
  }

  const formatDateMMDDYY = (date: string | Date | null | undefined) => {
    if (!date) return '--'
    const dayjsDate = dayjs(date)
    if (!dayjsDate.isValid()) return '--'
    return dayjsDate.format('MM/DD/YYYY')
  }
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page)
    fetchTherapists(page)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    sessionStorage.setItem('prevSize', newPageSize.toString())
  }
  const fetchTherapists = async (page?: number) => {
    setLoading(true)
    setError('')
    try {
      setLoadingForShimmer(true)
      const params: Record<string, any> = {
        accountStatus:
          selectedAccountStatus === 'Active'
            ? true
            : selectedAccountStatus === 'Inactive'
              ? false
              : undefined,
        page: page || 1, // Default to 1 if page is not provided
        limit: 10,
      }

      // If search keyword exists, add it to params
      if (searchKeyword?.trim()) {
        params.search = searchKeyword
      }

      if (startDate) {
        params.startDate = getFormattedDateForAPI(startDate)
      }

      if (endDate) {
        params.endDate = getFormattedDateForAPI(endDate)
      }

      if (licenseExpiryDate) {
        params.expiry = getFormattedDateForAPI(licenseExpiryDate)
      }

      // Add sorting parameters
      if (sortField) {
        params.sortField = sortField
        params.sortOrder = sortOrder
      }

      // Make the API call with params
      const response = await api.get('admin/therapists', {
        params,
      })

      // Check if data exists and set the therapists state
      if (response?.data?.data) {
        setTherapists(response.data.data)
      } else {
        setError('No providers data found')
      }
      setTotalRecords(response?.data?.stats?.totalTherapists)
      setTotalActiveRecords(response?.data?.stats?.totalActiveTherapists)
      setTotalInactiveRecords(response?.data?.stats?.totalInactiveTherapists)
      setTotalPages(Math.ceil(response?.data?.totalRecords / pageSize))
    } catch (err: any) {
      console.error(err)
      setError('Failed to fetch providers')
    } finally {
      setLoadingForShimmer(false)
      setLoading(false)
    }
  }
  const handleReset = () => {
    setSearchKeyword('')
    setSelectedAccountStatus('')
    setStartDate(null)
    setEndDate(null)
    setLicenseExpiryDate(null)
    setSortField('')
    setSortOrder('asc')
  }

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle sort order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      // Set new field and default to ascending
      setSortField(field)
      setSortOrder('asc')
    }
  }

  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return <UnfoldMoreIcon className="w-4 h-4 ml-1" />
    }
    return sortOrder === 'asc' ? (
      <KeyboardArrowUpIcon className="w-4 h-4 ml-1" />
    ) : (
      <KeyboardArrowDownIcon className="w-4 h-4 ml-1" />
    )
  }
  // const handleOpenToggle = async (
  //   providerId: string,
  //   currentStatus: boolean
  // ) => {
  //   try {
  //     // Toggle the status (flip the boolean value of isActive)
  //     const newStatus = !currentStatus

  //     // Call the API to update the therapist's status
  //     const response = await api.patch(
  //       `/admin/therapist/${providerId}/status`,
  //       {
  //         isActive: newStatus,
  //       }
  //     )

  //     if (response.status === 200) {
  //       // On success, toggle the status locally
  //       setIsActive(newStatus) // Update the state to reflect the new status
  //       toast.success(
  //         `Therapist status updated to ${newStatus ? 'Active' : 'Inactive'}`
  //       )
  //     }

  //     // Optionally, refresh the list of therapists
  //     fetchTherapists()
  //   } catch (error) {
  //     console.error('Error updating the therapist status:', error)
  //     toast.error('Failed to update therapist status')
  //   }
  // }

  useEffect(() => {
    fetchTherapists(1)
  }, [
    searchKeyword,
    selectedAccountStatus,
    startDate,
    endDate,
    licenseExpiryDate,
    sortField,
    sortOrder,
  ])
  return (
    <div className="p-6 mx-auto max-w-7xl">
      {/* Header & Filters */}
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-4 mb-4 sm:flex-row sm:items-center">
        <h1 className="text-2xl font-semibold text-[#6E7B8B]">
          Providers Management
        </h1>
        <button
          className="flex items-center gap-2 px-4 py-2 text-white bg-[#5BC0BE] border border-gray-300 rounded-lg hover:bg-[#49a9a7] disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleExport}
          disabled={exportLoading}
          style={{
            cursor: exportLoading ? 'not-allowed' : 'pointer',
          }}
        >
          <FiDownload className="w-4 h-4" />
          {exportLoading ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      {/* Search & Filters Row */}
      <Card
        sx={{
          height: '100%',
          cursor: 'pointer',
          boxShadow: 2,
          padding: 2,
          mb: 3,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 4,
          },
        }}
      >
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-6">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Search Provider
              </label>
              <input
                type="text"
                value={searchKeyword}
                onChange={e => setSearchKeyword(e.target.value)}
                placeholder="Search Provider"
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#026730] focus:border-transparent"
              />
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Account Status
              </label>
              <select
                id="accountStatus"
                value={selectedAccountStatus}
                onChange={handleAccountStatusChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#026730] focus:border-transparent"
              >
                <option value="" disabled selected>
                  Select Status
                </option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box display="flex" alignItems="flex-start" gap={2} width="100%">
                <Box>
                  <Typography variant="body2" mb={0.5}>
                    Start Date
                  </Typography>
                  <DatePicker
                    value={startDate}
                    onChange={(newValue: Date | null) => setStartDate(newValue)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: false,
                        sx: { width: '150px' },
                      },
                    }}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" mb={0.5}>
                    End Date
                  </Typography>
                  <DatePicker
                    value={endDate}
                    onChange={(newValue: Date | null) => setEndDate(newValue)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: false,
                        sx: { width: '150px' },
                      },
                    }}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" mb={0.5}>
                    License Expiry
                  </Typography>
                  <DatePicker
                    value={licenseExpiryDate}
                    onChange={(newValue: Date | null) =>
                      setLicenseExpiryDate(newValue)
                    }
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: false,
                        sx: { width: '150px' },
                      },
                    }}
                  />
                </Box>

                {/* Reset All Button */}
                <Box display="flex" alignItems="center">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleReset}
                    sx={{
                      borderRadius: 2,
                      width: '113px',
                      marginTop: '24px',
                    }}
                  >
                    Reset All
                  </Button>
                </Box>
              </Box>
            </LocalizationProvider>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-3">
        <div className="flex items-center justify-between p-4 bg-white rounded-lg shadow h-35">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{totalRecords}</h2>
            <p className="text-sm text-gray-500">Total Providers</p>
          </div>
          <div className="flex items-center justify-center w-12 h-12 text-xl font-bold text-green-600 bg-green-100 rounded-full">
            <PersonIcon />
          </div>
        </div>

        <div className="flex items-center justify-between p-4 bg-white rounded-lg shadow">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {totalActiveRecords}
            </h2>
            <p className="text-sm text-gray-500">Active Accounts</p>
          </div>
          <div className="flex items-center justify-center w-12 h-12 text-xl font-bold text-blue-600 bg-blue-100 rounded-full">
            <CheckIcon />
          </div>
        </div>

        <div className="flex items-center justify-between p-4 bg-white rounded-lg shadow">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {totalInactiveRecords}
            </h2>
            <p className="text-sm text-gray-500">InActive Accounts</p>
          </div>
          <div className="flex items-center justify-center w-12 h-12 text-xl font-bold text-red-600 bg-red-100 rounded-full">
            <ClearIcon />
          </div>
        </div>
      </div>

      {/* Therapist Table */}
      <div className="overflow-hidden bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr className="text-sm text-left text-white uppercase bg-[#5BC0BE]">
                <th
                  className="px-6 py-3 font-medium cursor-pointer"
                  onClick={() => handleSort('firstName')}
                >
                  <div className="flex items-center">
                    Name
                    {getSortIcon('firstName')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 font-medium cursor-pointer"
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center">
                    Email
                    {getSortIcon('email')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 font-medium cursor-pointer"
                  onClick={() => handleSort('phone')}
                >
                  <div className="flex items-center">
                    Phone
                    {getSortIcon('phone')}
                  </div>
                </th>

                <th
                  className="px-6 py-3 font-medium cursor-pointer"
                  onClick={() => handleSort('gender')}
                >
                  <div className="flex items-center">
                    Gender
                    {getSortIcon('gender')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 font-medium cursor-pointer"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center">
                    Onboarding
                    {getSortIcon('createdAt')}
                  </div>
                </th>
                <th className="px-6 py-3 font-medium">License No</th>
                <th className="px-6 py-3 font-medium">License Expiration</th>
                <th className="px-6 py-3 font-medium">Accepted Insurances</th>

                {/* <th className="px-6 py-3 font-medium">Service Packages</th> */}
                <th className="px-6 py-3 font-medium">Account Status</th>
                <th className="px-6 py-3 font-medium text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loadingForShimmer ? (
                <TableShimmer columns={11} />
              ) : therapists?.length === 0 ? (
                <tr>
                  <td colSpan={20} className="p-4 text-center">
                    <Notfound />
                  </td>
                </tr>
              ) : (
                therapists.map(therapist => (
                  <tr
                    key={therapist._id}
                    className="transition-colors hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 min-w-[200px]">
                      {therapist?.firstName} {therapist?.lastName}
                    </td>
                    <td className="px-6 py-4">{therapist?.email}</td>
                    <td className="px-6 py-4 min-w-[200px]">
                      {therapist?.phone}
                    </td>

                    <td className="px-6 py-4">
                      {therapist.gender
                        ? therapist?.gender.charAt(0).toUpperCase() +
                          therapist?.gender.slice(1).toLowerCase()
                        : ''}
                    </td>
                    <td className="px-6 py-4">
                      {formatDateMMDDYY(therapist?.createdAt) || '--'}
                    </td>
                    <td className="px-6 py-4 min-w-[280px]">
                      {therapist?.licenses && therapist.licenses.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {therapist.licenses
                            .filter((license: any) => license.licenseNumber)
                            .map((license: any, index: number) => (
                              <span
                                key={index}
                                className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 border border-blue-200 rounded-full"
                              >
                                {license.licenseNumber}
                              </span>
                            ))}
                        </div>
                      ) : (
                        '--'
                      )}
                    </td>
                    <td className="px-6 py-4 min-w-[200px]">
                      {therapist?.licenses && therapist.licenses.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {therapist.licenses
                            .filter((license: any) => license.expiryDate)
                            .map((license: any, index: number) => {
                              const expiry = new Date(license.expiryDate)
                              if (isNaN(expiry.getTime())) return null
                              const month = expiry.getUTCMonth() + 1
                              const day = expiry.getUTCDate()
                              const year = expiry.getUTCFullYear()
                              const formattedDate = `${month}/${day}/${year}`
                              const isExpired = expiry < new Date()
                              const isExpiringSoon =
                                expiry >= new Date() &&
                                expiry <=
                                  new Date(
                                    new Date().setMonth(
                                      new Date().getMonth() + 3
                                    )
                                  )

                              return (
                                <span
                                  key={index}
                                  className={`px-2 py-1 text-xs font-medium rounded-full border ${
                                    isExpired
                                      ? 'text-red-700 bg-red-100 border-red-200'
                                      : isExpiringSoon
                                        ? 'text-yellow-700 bg-yellow-100 border-yellow-200'
                                        : 'text-green-700 bg-green-100 border-green-200'
                                  }`}
                                >
                                  {formattedDate}
                                </span>
                              )
                            })}
                        </div>
                      ) : (
                        '--'
                      )}
                    </td>
                    <td className="px-6 py-4 min-w-[200px]">
                      {therapist?.insuranceAccepted &&
                      therapist.insuranceAccepted.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {therapist.insuranceAccepted
                            .filter((insurance: any) => insurance.name)
                            .map((insurance: any, index: number) => (
                              <span
                                key={index}
                                className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 border border-green-200 rounded-full"
                              >
                                {insurance.name}
                              </span>
                            ))}
                        </div>
                      ) : (
                        '--'
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium border ${
                          therapist?.isActive
                            ? 'border-green-500 text-green-600'
                            : 'border-red-500 text-red-600'
                        }`}
                      >
                        {therapist?.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Tooltip title="Set Commission" arrow>
                          <IconButton
                            onClick={() =>
                              handleOpenCommissionDialog(therapist)
                            }
                            size="small"
                            sx={{
                              color: '#2E7DAF',
                              '&:hover': {
                                bgcolor: 'rgba(46, 125, 175, 0.1)',
                              },
                            }}
                          >
                            <PercentIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            therapist?.isActive ? 'Deactivate' : 'Activate'
                          }
                          arrow
                        >
                          <IconButton
                            onClick={() =>
                              handleOpenToggle(
                                therapist?._id,
                                therapist?.isActive
                              )
                            }
                          >
                            {therapist?.isActive ? (
                              <ToggleOnIcon
                                sx={{ fontSize: 32, color: '#10b981' }}
                              />
                            ) : (
                              <ToggleOffIcon
                                sx={{ fontSize: 32, color: '#ef4444' }}
                              />
                            )}
                          </IconButton>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex justify-center mt-3">
        <div className="flex items-center gap-4">
          <RowsPerPageDropdown
            pageSize={pageSize}
            onPageSizeChange={newPageSize => {
              handlePageSizeChange(newPageSize)
              if (ourPageSize && ourPageSize !== newPageSize) {
                setCurrentPage(1)
              }
            }}
          />
        </div>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          className="mt-1 mt-2 mt-md-0"
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
        />
      </div>
      <ToastContainer />
      <Dialog open={openToggle} onClose={() => setOpenToggle(false)}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          Are you sure you want to {isActive ? 'deactivate' : 'activate'} this
          provider?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenToggle(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmToggle}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Updating...' : isActive ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Commission Dialog */}
      <Dialog
        open={openCommissionDialog}
        onClose={handleCloseCommissionDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Set Provider Commission</DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            pt: '16px !important',
          }}
        >
          <TextField
            label="Monthly Commission"
            type="number"
            value={monthlyCommission}
            onChange={e => {
              const inputValue = e.target.value
              // Allow only positive decimal numbers (up to 2 decimal places)
              if (inputValue === '' || parseFloat(inputValue) >= 0) {
                const validValue = inputValue.match(/^(\d+(\.\d{0,2})?)?$/)
                  ? inputValue
                  : monthlyCommission
                setMonthlyCommission(validValue)
              }
            }}
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            helperText="Enter the monthly commission percentage"
          />
          <TextField
            label="Yearly Commission"
            type="number"
            value={yearlyCommission}
            onChange={e => {
              const inputValue = e.target.value
              // Allow only positive decimal numbers (up to 2 decimal places)
              if (inputValue === '' || parseFloat(inputValue) >= 0) {
                const validValue = inputValue.match(/^(\d+(\.\d{0,2})?)?$/)
                  ? inputValue
                  : yearlyCommission
                setYearlyCommission(validValue)
              }
            }}
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            helperText="Enter the yearly commission percentage"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseCommissionDialog}
            disabled={commissionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateCommission}
            variant="contained"
            color="primary"
            disabled={commissionLoading}
          >
            {commissionLoading ? 'Updating...' : 'Update Commission'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default ProviderManagementPage
